import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { handleImageErrorWithFallback } from '../../utils/imageManager';
import { renderCtaButton } from '../../utils/ctaHelper';
import { usePageTitle } from '../../hooks/usePageTitle';

const MagazineVariant = ({ heroContent, brand }) => {
  const { cta, domainConfig } = useTheme();
  const mainImageRef = useRef(null);
  const secondaryImageRef = useRef(null);
  
  // Get hostname-specific H1 title
  const h1Title = usePageTitle('home');

  // Reset image elements when domain changes
  useEffect(() => {
    if (mainImageRef.current) {
      Object.keys(mainImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete mainImageRef.current.dataset[key];
        }
      });
    }
    if (secondaryImageRef.current) {
      Object.keys(secondaryImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete secondaryImageRef.current.dataset[key];
        }
      });
    }
  }, [brand.id]);

  const { mainImage, secondaryImage, title, subtitle, features } = heroContent;

  return (
    <div className="min-h-screen bg-white">
      {/* Magazine Header */}
      <section className="pt-20 pb-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {brand.name}
            </div>
            <div className="text-xs text-gray-400">EST. 2024 • ISSUE 01</div>
          </div>
        </div>
      </section>

      {/* Hero Section - Magazine Style */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Story - Large Column */}
            <div className="lg:col-span-2">
              <div className="relative">
                <img 
                  ref={mainImageRef}
                  src={mainImage} 
                  alt={title}
                  className="w-full h-96 lg:h-[500px] object-cover"
                  onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-main')}
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8">
                  <div className="text-white">
                    <div className="text-sm font-semibold uppercase tracking-wider text-white/80 mb-2">
                      Cover Story
                    </div>
                    <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-4">
                      {h1Title || title}
                    </h1>
                    <p className="text-lg text-white/90 leading-relaxed">
                      {subtitle}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar - Small Column */}
            <div className="lg:col-span-1">
              <div className="space-y-8">
                {/* Secondary Image */}
                <div className="relative">
                  <img 
                    ref={secondaryImageRef}
                    src={secondaryImage} 
                    alt={`${title} - Secondary`}
                    className="w-full h-48 object-cover"
                    onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-secondary')}
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-red-600 text-white px-3 py-1 text-xs font-bold uppercase tracking-wider">
                      Exclusive
                    </span>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="bg-gray-50 p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">By The Numbers</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Active Users</span>
                      <span className="font-bold text-gray-900">500+</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Uptime</span>
                      <span className="font-bold text-gray-900">99.9%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Support</span>
                      <span className="font-bold text-gray-900">24/7</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Rating</span>
                      <span className="font-bold text-gray-900">5★</span>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="bg-black text-white p-6">
                  <h3 className="text-lg font-bold mb-4">Get Started Today</h3>
                  <p className="text-sm text-gray-300 mb-4">
                    Join thousands of satisfied customers
                  </p>
                  {renderCtaButton(cta?.primary, 'Start Free Trial', 'w-full bg-white text-black font-bold py-3 hover:bg-gray-100 transition-colors duration-200 text-center', domainConfig?.shopLink)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Magazine Layout */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Featured Stories
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Our Solutions</h2>
            <div className="w-24 h-1 bg-black mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <article key={index} className="bg-white">
                <div className="relative">
                  <div className="w-full h-48 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                    <div className="text-4xl text-gray-500">
                      {index % 4 === 0 ? '📊' : index % 4 === 1 ? '⚡' : index % 4 === 2 ? '🛡️' : '⭐'}
                    </div>
                  </div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-white text-black px-2 py-1 text-xs font-bold uppercase tracking-wider">
                      {index + 1}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                    {feature}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    Professional-grade solution designed to meet enterprise requirements with comprehensive support and documentation.
                  </p>
                  <button className="text-black font-semibold text-sm uppercase tracking-wider hover:underline">
                    Read More →
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Editorial */}
            <div className="lg:col-span-2">
              <div className="mb-8">
                <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Editorial
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  The Future of Business Solutions
                </h2>
                <div className="w-16 h-1 bg-black mb-6"></div>
              </div>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6">
                  In today's rapidly evolving business landscape, organizations need solutions that not only meet their current needs but also adapt to future challenges. Our comprehensive approach ensures that every aspect of your business is covered with precision and efficiency.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  We provide structured solutions that scale with your needs while maintaining the highest standards of quality and performance. Our team of experts works tirelessly to deliver innovative solutions that drive real business value.
                </p>
                <blockquote className="border-l-4 border-black pl-6 italic text-gray-700 text-lg">
                  "The best way to predict the future is to create it. Our solutions help you do exactly that."
                </blockquote>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="space-y-8">
                {/* Table of Contents */}
                <div className="bg-gray-50 p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Table of Contents</h3>
                  <div className="space-y-2">
                    <button className="block text-sm text-gray-600 hover:text-black transition-colors duration-200 text-left">
                      1. Introduction
                    </button>
                    <button className="block text-sm text-gray-600 hover:text-black transition-colors duration-200 text-left">
                      2. Key Features
                    </button>
                    <button className="block text-sm text-gray-600 hover:text-black transition-colors duration-200 text-left">
                      3. Implementation
                    </button>
                    <button className="block text-sm text-gray-600 hover:text-black transition-colors duration-200 text-left">
                      4. Case Studies
                    </button>
                    <button className="block text-sm text-gray-600 hover:text-black transition-colors duration-200 text-left">
                      5. Conclusion
                    </button>
                  </div>
                </div>

                {/* Author Info */}
                <div className="bg-white border border-gray-200 p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">About the Author</h3>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 bg-gray-300 rounded-full"></div>
                    <div>
                      <div className="font-semibold text-gray-900">John Smith</div>
                      <div className="text-sm text-gray-600">Editor-in-Chief</div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    With over 15 years of experience in business technology, John leads our editorial team in delivering insightful content.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <div className="text-sm font-semibold uppercase tracking-wider mb-2">
              {brand.name}
            </div>
            <div className="text-xs text-gray-400 mb-8">
              EST. 2024 • ISSUE 01 • VOLUME 1
            </div>
            <div className="w-24 h-1 bg-white mx-auto"></div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MagazineVariant;
